"use client";

import {
	confusionFlags,
	identityAccuracy,
	personAttributes,
	personSnapshot,
} from "@marketing/shared/lib/bento-fixtures";
import { Badge } from "@ui/components/badge";
import { DeltaBadge } from "@ui/components/delta-badge";
import { cn } from "@ui/lib";
import { CHART_SERIES } from "@ui/lib/chart-palette";
import {
	BookOpenIcon,
	BriefcaseIcon,
	MapPinIcon,
	MicIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useRevealed } from "./motion";

/**
 * Production person-tracking tiles for /person-tracking. Sample data (the
 * fictional Dana Whitfield) stays English in both locales, like a
 * screenshot would; every piece of widget chrome reads from the
 * marketing.personTracking.widgets namespace. The design lab keeps its own
 * EN-only copies for mockups.
 */

export function IdentitySnapshotCard({ className }: { className?: string }) {
	return (
		<div className={cn("space-y-4", className)}>
			<div className="flex items-center gap-3">
				<span className="flex size-12 shrink-0 items-center justify-center rounded-full border bg-muted font-bold text-muted-foreground">
					{personSnapshot.initials}
				</span>
				<div className="min-w-0">
					<p className="truncate text-base font-bold">
						{personSnapshot.name}
					</p>
					<p className="truncate text-sm text-muted-foreground">
						{personSnapshot.role}
					</p>
					<p className="truncate text-sm text-muted-foreground">
						{personSnapshot.company}
					</p>
				</div>
			</div>
			<ul className="space-y-2 text-sm">
				<li className="flex items-center gap-2 text-muted-foreground">
					<MapPinIcon className="size-4 shrink-0" aria-hidden />
					{personSnapshot.location}
				</li>
				<li className="flex items-center gap-2 text-muted-foreground">
					<BriefcaseIcon className="size-4 shrink-0" aria-hidden />
					{personSnapshot.knownFor[0]}
				</li>
				<li className="flex items-center gap-2 text-muted-foreground">
					<BookOpenIcon className="size-4 shrink-0" aria-hidden />
					{personSnapshot.knownFor[1]}
				</li>
				<li className="flex items-center gap-2 text-muted-foreground">
					<MicIcon className="size-4 shrink-0" aria-hidden />
					{personSnapshot.knownFor[2]}
				</li>
			</ul>
			<p className="border-t pt-3 text-xs text-muted-foreground">
				{personSnapshot.sources}
			</p>
		</div>
	);
}

/** Per-platform Identity Accuracy bars, with/without browsing. */
export function IdentityAccuracyTile({ className }: { className?: string }) {
	const t = useTranslations("marketing.personTracking.widgets");
	const revealed = useRevealed();
	return (
		<div className={cn("space-y-3", className)}>
			{identityAccuracy.perPlatform.map((platform) => (
				<div key={platform.key}>
					<div className="flex items-baseline justify-between text-xs">
						<span className="font-medium">{platform.label}</span>
						<span className="font-mono tabular-nums text-muted-foreground">
							{t("accuracyRow", {
								withBrowsing: platform.withBrowsing,
								withoutBrowsing: platform.withoutBrowsing,
							})}
						</span>
					</div>
					<div className="mt-1 space-y-1">
						{(
							[
								[platform.withBrowsing, 1],
								[platform.withoutBrowsing, 0.4],
							] as const
						).map(([value, opacity]) => (
							<div
								key={`${platform.key}-${opacity}`}
								className="h-1.5 rounded-full bg-muted"
							>
								<div
									className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
									style={{
										width: revealed ? `${value}%` : "0%",
										backgroundColor: CHART_SERIES[0],
										opacity,
									}}
								/>
							</div>
						))}
					</div>
				</div>
			))}
			<p className="pt-1 text-[11px] text-muted-foreground">
				{t("accuracyLegend")}
			</p>
		</div>
	);
}

/** Tracked narrative attributes with nightly mention rates. */
export function AttributeTrackingTile({ className }: { className?: string }) {
	const t = useTranslations("marketing.personTracking.widgets");
	const revealed = useRevealed();
	return (
		<ul className={cn("space-y-3.5", className)}>
			{personAttributes.map((attr) => {
				const negative = attr.sentiment === "negative";
				return (
					<li key={attr.message}>
						<div className="flex items-center justify-between gap-2">
							<span className="flex min-w-0 items-center gap-2">
								<Badge
									status={negative ? "warning" : "success"}
								>
									{negative
										? t("badgeWatching")
										: t("badgeAmplify")}
								</Badge>
								<span className="truncate text-sm font-medium">
									{attr.message}
								</span>
							</span>
							<span className="flex shrink-0 items-center gap-1.5">
								<span className="font-mono text-sm font-semibold tabular-nums">
									{attr.mentionRate}%
								</span>
								<DeltaBadge
									change={attr.trend}
									suffix=" pts"
									lowerIsBetter={negative}
								/>
							</span>
						</div>
						<div className="mt-1.5 h-1.5 rounded-full bg-muted">
							<div
								className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
								style={{
									width: revealed
										? `${attr.mentionRate}%`
										: "0%",
									backgroundColor: negative
										? "var(--muted-foreground)"
										: CHART_SERIES[0],
									opacity: negative ? 0.45 : 1,
								}}
							/>
						</div>
					</li>
				);
			})}
			<li className="pt-0.5 text-[11px] text-muted-foreground">
				{t("attributesLegend")}
			</li>
		</ul>
	);
}

/** Same-name confusion and outdated-info flags. `kind` filters to one. */
export function ConfusionFlagCards({
	kind,
	className,
}: {
	kind?: "same-name" | "outdated";
	className?: string;
}) {
	const t = useTranslations("marketing.personTracking.widgets");
	const flags = kind
		? confusionFlags.filter((f) => f.kind === kind)
		: confusionFlags;
	return (
		<div className={cn("space-y-3", className)}>
			{flags.map((flag) => (
				<div
					key={flag.claim}
					className="rounded-xl border bg-background p-4"
				>
					<div className="flex items-center gap-2">
						<Badge
							status={
								flag.kind === "same-name" ? "error" : "warning"
							}
						>
							{flag.kind === "same-name"
								? t("badgeSameName")
								: t("badgeOutdated")}
						</Badge>
						<span className="text-xs font-medium text-muted-foreground">
							{flag.platform}
						</span>
					</div>
					<p className="mt-2 text-sm italic text-foreground/90">
						{flag.claim}
					</p>
					<p className="mt-1.5 text-xs text-muted-foreground">
						{flag.note}
					</p>
				</div>
			))}
		</div>
	);
}
